import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { fetchBatchById } from "../pages/services/batchService";
import { sendBatchNotification, fetchWhatsAppTemplates } from "../pages/services/studentService";
import { fetchAssignmentsByBatchId } from "../pages/services/assignmentService";
import { fetchLogsByBatchId } from "../pages/services/logService";
import { fetchAllSubjectsSimple } from "../pages/services/courseService";
import NotificationHistorySection from "./NotificationHistorySection";
import "../styles/BatchDetails.css";
import "../styles/StudentDetails.css";

const BUDDY_BASE = "https://buddy.myskillverse.ai/assignment.html";

const PARAM_OPTIONS = [
  { value: "student_name", label: "Student Name" },
  { value: "parent_name", label: "Parent Name" },
  { value: "batch_name", label: "Batch Name" },
  { value: "assignment_link", label: "Assignment Link" },
  { value: "custom", label: "Custom" },
];

const buildBatchPreview = (body, paramNames, paramSelections, batch) => {
  if (!body) return "";
  let msg = body;
  paramNames.forEach((_, idx) => {
    const sel = paramSelections[idx];
    let val = "";
    if (!sel || !sel.type) {
      val = `{{${idx + 1}}}`;
    } else if (sel.type === "student_name") {
      val = "[Student Name]";
    } else if (sel.type === "parent_name") {
      val = "[Parent Name]";
    } else if (sel.type === "batch_name") {
      val = batch?.batchTitle || "[Batch Name]";
    } else if (sel.type === "assignment_link") {
      val = sel.value || "[Assignment Link]";
    } else if (sel.type === "custom") {
      val = sel.value || "[Custom]";
    }
    msg = msg.replace(`{{${idx + 1}}}`, val);
  });
  return msg;
};

const buildEmailBodyPreview = (body, paramSelections, batch) => {
  if (!body) return "";
  let text = body;
  Object.entries(paramSelections).forEach(([num, sel]) => {
    let val = `{${num}}`;
    if (sel.type === "student_name") val = "[Student Name]";
    else if (sel.type === "parent_name") val = "[Parent Name]";
    else if (sel.type === "batch_name") val = batch?.batchTitle || "[Batch Name]";
    else if (sel.type === "assignment_link") val = sel.value || "[Assignment Link]";
    else if (sel.type === "custom") val = sel.value || "[Custom]";
    text = text.replaceAll(`{${num}}`, val);
  });
  return text;
};

/* ── Batch Notification Modal ───────────────────────────────── */
const BatchNotificationModal = ({ isOpen, onClose, batch, studentCount, assignments }) => {
  const [notifType, setNotifType] = useState("email");
  const [emailTitle, setEmailTitle] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailParamSelections, setEmailParamSelections] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sentTypes, setSentTypes] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paramSelections, setParamSelections] = useState([]);
  const fileInputRef = useRef(null);

  const emailParamNums = emailBody
    ? [...new Set([...emailBody.matchAll(/\{(\d+)\}/g)].map(m => m[1]))]
        .sort((a, b) => Number(a) - Number(b))
    : [];

  useEffect(() => {
    if (notifType === "whatsapp" && templates.length === 0) {
      setTemplatesLoading(true);
      fetchWhatsAppTemplates()
        .then(setTemplates)
        .catch(() => setTemplates([]))
        .finally(() => setTemplatesLoading(false));
    }
  }, [notifType]);

  const paramNames = selectedTemplate?.params
    ? selectedTemplate.params.split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  const previewText = buildBatchPreview(
    selectedTemplate?.body || "",
    paramNames,
    paramSelections,
    batch
  );

  const resetForm = () => {
    setNotifType("email");
    setEmailTitle("");
    setEmailBody("");
    setEmailParamSelections({});
    setAttachments([]);
    setShowPreview(false);
    setResult(null);
    setSentTypes(new Set());
    setSelectedTemplate(null);
    setParamSelections([]);
  };

  const handleEmailParamTypeChange = (num, type) => {
    setEmailParamSelections(prev => ({ ...prev, [num]: { type, value: "", assignmentId: "" } }));
  };

  const handleEmailParamValueChange = (num, value) => {
    setEmailParamSelections(prev => ({ ...prev, [num]: { ...prev[num], value } }));
  };

  const handleEmailParamAssignmentChange = (num, assignmentId) => {
    const asgn = (assignments || []).find(a => a._id === assignmentId);
    setEmailParamSelections(prev => ({
      ...prev,
      [num]: { type: "assignment_link", assignmentId, value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : "" },
    }));
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleAttachmentChange = (e) => {
    setAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = "";
  };

  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const handleTemplateChange = (templateId) => {
    const tmpl = templates.find((t) => t._id === templateId) || null;
    setSelectedTemplate(tmpl);
    if (tmpl) {
      const names = tmpl.params ? tmpl.params.split(",").map((p) => p.trim()).filter(Boolean) : [];
      setParamSelections(names.map(() => ({ type: "", value: "" })));
    } else {
      setParamSelections([]);
    }
  };

  const handleParamTypeChange = (idx, type) => {
    setParamSelections((prev) => {
      const next = [...prev];
      next[idx] = { type, value: "" };
      return next;
    });
  };

  const handleParamValueChange = (idx, value) => {
    setParamSelections((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], value };
      return next;
    });
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type.startsWith("video/")) return "🎬";
    if (file.type === "application/pdf") return "📄";
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canSendWhatsApp = selectedTemplate &&
    paramNames.every((_, idx) => {
      const sel = paramSelections[idx];
      if (!sel?.type) return false;
      if (sel.type === "custom" && !sel.value?.trim()) return false;
      if (sel.type === "assignment_link" && !sel.value?.trim()) return false;
      return true;
    });

  const canSend = notifType === "email"
    ? (emailTitle.trim() || emailBody.trim())
    : canSendWhatsApp;

  const handleSend = async () => {
    setIsSending(true);
    setResult(null);
    try {
      const res = await sendBatchNotification(
        batch._id,
        notifType,
        emailTitle,
        emailBody,
        attachments,
        notifType === "whatsapp"
          ? { templateName: selectedTemplate.templateName, paramSelections }
          : null,
        notifType === "email" && emailParamNums.length > 0
          ? { paramSelections: emailParamSelections }
          : null
      );
      setResult({ success: true, sent: res.sent, failed: res.failed, details: res.details });
      setSentTypes(prev => new Set([...prev, notifType]));
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notif-overlay" onClick={handleClose}>
      <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="notif-modal-header">
          <h2>Send Batch Notification</h2>
          <button className="notif-close-btn" onClick={handleClose}>×</button>
        </div>

        {/* Body */}
        <div className="notif-modal-body">
          {/* Channel selector */}
          <div className="notif-radio-group">
            <label className={`notif-radio-option ${notifType === "email" ? "notif-radio-selected" : ""}`}>
              <input type="radio" name="batchNotifType" value="email" checked={notifType === "email"}
                onChange={() => { setNotifType("email"); setShowPreview(false); }} />
              <span className="notif-radio-icon">✉️</span> Email
            </label>
            <label className={`notif-radio-option ${notifType === "whatsapp" ? "notif-radio-selected" : ""}`}>
              <input type="radio" name="batchNotifType" value="whatsapp" checked={notifType === "whatsapp"}
                onChange={() => { setNotifType("whatsapp"); setShowPreview(false); }} />
              <span className="notif-radio-icon">💬</span> WhatsApp
            </label>
          </div>

          {/* Recipient info banner */}
          <div className="bd-notif-recipients">
            <span className="bd-notif-recipients-icon">👥</span>
            <span>
              Sending to <strong>{studentCount}</strong> student{studentCount !== 1 ? "s" : ""} in{" "}
              <strong>{batch?.batchTitle}</strong>
            </span>
          </div>

          {/* Email fields */}
          {notifType === "email" && (
            <div className="notif-email-fields">
              <div className="notif-field">
                <label className="notif-label">Subject</label>
                <input type="text" value={emailTitle} onChange={(e) => setEmailTitle(e.target.value)}
                  placeholder="Enter email subject…" className="notif-input" />
              </div>
              <div className="notif-field">
                <label className="notif-label">
                  Body
                  <span className="notif-param-hint">Use {"{1}"}, {"{2}"}… as placeholders — dropdowns appear below</span>
                </label>
                <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
                  placeholder={"Write your email content here…\nTip: use {1}, {2} for dynamic values like student name, assignment link, etc."}
                  className="notif-textarea" rows={5} />
              </div>

              {emailParamNums.length > 0 && (
                <div className="notif-field">
                  <label className="notif-label">Placeholder Values</label>
                  <div className="wa-params-list">
                    {emailParamNums.map((num) => (
                      <div key={num} className="wa-param-row">
                        <span className="wa-param-name">{`{${num}}`}</span>
                        <div className="wa-param-select-wrap">
                          <select
                            className="notif-input wa-select"
                            value={emailParamSelections[num]?.type || ""}
                            onChange={(e) => handleEmailParamTypeChange(num, e.target.value)}
                          >
                            <option value="">Select value…</option>
                            {PARAM_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {emailParamSelections[num]?.type === "custom" && (
                            <textarea
                              className="notif-textarea wa-custom-textarea"
                              rows={2}
                              placeholder="Enter custom value or URL…"
                              value={emailParamSelections[num]?.value || ""}
                              onChange={(e) => handleEmailParamValueChange(num, e.target.value)}
                            />
                          )}
                          {emailParamSelections[num]?.type === "assignment_link" && (
                            <div className="wa-assignment-picker">
                              <select
                                className="notif-input wa-select"
                                value={emailParamSelections[num]?.assignmentId || ""}
                                onChange={(e) => handleEmailParamAssignmentChange(num, e.target.value)}
                              >
                                <option value="">Select assignment…</option>
                                {(assignments || []).map((a) => {
                                  const parts = (a.assignmentTime || "").split(" - ");
                                  const fmt = (s) => {
                                    const d = new Date(s?.trim());
                                    return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
                                  };
                                  const window = parts.length === 2 ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}` : "";
                                  return <option key={a._id} value={a._id}>{a.assignmentTitle}{window}</option>;
                                })}
                              </select>
                              {emailParamSelections[num]?.value && (
                                <span className="wa-assignment-link-preview">{emailParamSelections[num].value}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="notif-field">
                <label className="notif-label">Attachments</label>
                <input ref={fileInputRef} type="file" multiple onChange={handleAttachmentChange}
                  className="notif-file-input-hidden" />
                <button type="button" className="notif-file-btn" onClick={() => fileInputRef.current?.click()}>
                  + Choose Files
                </button>
                <span className="notif-file-hint">PDF, Images, Videos and more supported</span>
                {attachments.length > 0 && (
                  <div className="notif-attachments-list">
                    {attachments.map((file, idx) => (
                      <div key={idx} className="notif-attachment-chip">
                        <span className="notif-attachment-icon">{getFileIcon(file)}</span>
                        <span className="notif-attachment-name">{file.name}</span>
                        <span className="notif-attachment-size">({formatFileSize(file.size)})</span>
                        <button className="notif-attachment-remove" onClick={() => removeAttachment(idx)}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="button" className="notif-preview-toggle" onClick={() => setShowPreview((p) => !p)}>
                {showPreview ? "▲ Hide Preview" : "▼ Show Preview"}
              </button>

              {showPreview && (
                <div className="notif-preview-wrapper">
                  <div className="notif-preview-bar">
                    <span>Email Preview</span>
                    <span className="notif-preview-hint">As seen by each recipient</span>
                  </div>
                  <div className="notif-email-preview">
                    <div className="preview-email-chrome">
                      <div className="preview-chrome-dots"><span /><span /><span /></div>
                      <span className="preview-chrome-title">Mail</span>
                    </div>
                    <div className="preview-email-body-wrapper">
                      <div className="preview-meta-row">
                        <span className="preview-field-label">From</span>
                        <span className="preview-field-value">SkillVerse &lt;noreply@skillverse.in&gt;</span>
                      </div>
                      <div className="preview-meta-row">
                        <span className="preview-field-label">To</span>
                        <span className="preview-field-value">
                          All students in {batch?.batchTitle} ({studentCount} recipient{studentCount !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <div className="preview-meta-row preview-subject-row">
                        <span className="preview-subject-text">
                          {emailTitle || <em className="preview-empty">No subject</em>}
                        </span>
                      </div>
                      <div className="preview-email-content">
                        {emailBody
                          ? <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{buildEmailBodyPreview(emailBody, emailParamSelections, batch)}</p>
                          : <p className="preview-empty">Email body will appear here…</p>}
                      </div>
                      {attachments.length > 0 && (
                        <div className="preview-attachments-row">
                          <span className="preview-attachments-label">📎 Attachments</span>
                          <div className="preview-attachment-chips">
                            {attachments.map((file, idx) => (
                              <span key={idx} className="preview-attachment-pill">
                                {getFileIcon(file)} {file.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp fields */}
          {notifType === "whatsapp" && (
            <div className="notif-email-fields">
              {/* Template dropdown */}
              <div className="notif-field">
                <label className="notif-label">Template</label>
                {templatesLoading ? (
                  <span className="notif-file-hint">Loading templates...</span>
                ) : (
                  <select
                    className="notif-input wa-select"
                    value={selectedTemplate?._id || ""}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((t) => (
                      <option key={t._id} value={t._id}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Params */}
              {selectedTemplate && paramNames.length > 0 && (
                <div className="notif-field">
                  <label className="notif-label">Parameters</label>
                  <div className="wa-params-list">
                    {paramNames.map((paramName, idx) => (
                      <div key={idx} className="wa-param-row">
                        <span className="wa-param-name">{paramName}</span>
                        <div className="wa-param-select-wrap">
                          <select
                            className="notif-input wa-select"
                            value={paramSelections[idx]?.type || ""}
                            onChange={(e) => handleParamTypeChange(idx, e.target.value)}
                          >
                            <option value="">Select value...</option>
                            {PARAM_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {paramSelections[idx]?.type === "custom" && (
                            <textarea
                              className="notif-textarea wa-custom-textarea"
                              rows={2}
                              placeholder="Enter custom value or URL..."
                              value={paramSelections[idx]?.value || ""}
                              onChange={(e) => handleParamValueChange(idx, e.target.value)}
                            />
                          )}
                          {paramSelections[idx]?.type === "assignment_link" && (
                            <div className="wa-assignment-picker">
                              <select
                                className="notif-input wa-select"
                                value={paramSelections[idx]?.assignmentId || ""}
                                onChange={(e) => {
                                  const asgn = (assignments || []).find(a => a._id === e.target.value);
                                  setParamSelections((prev) => {
                                    const next = [...prev];
                                    next[idx] = {
                                      type: "assignment_link",
                                      assignmentId: e.target.value,
                                      value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : "",
                                    };
                                    return next;
                                  });
                                }}
                              >
                                <option value="">Select assignment...</option>
                                {(assignments || []).map((a) => {
                                  const parts = (a.assignmentTime || "").split(" - ");
                                  const fmt = (s) => {
                                    const d = new Date(s?.trim());
                                    return isNaN(d) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
                                  };
                                  const window = parts.length === 2
                                    ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}`
                                    : "";
                                  return (
                                    <option key={a._id} value={a._id}>
                                      {a.assignmentTitle}{window}
                                    </option>
                                  );
                                })}
                              </select>
                              {paramSelections[idx]?.value && (
                                <span className="wa-assignment-link-preview">
                                  {paramSelections[idx].value}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WhatsApp Preview */}
              {selectedTemplate && (
                <div className="wa-preview-wrapper">
                  <div className="wa-preview-bar">
                    <span>WhatsApp Preview</span>
                    <span className="notif-preview-hint">Updates as you fill params</span>
                  </div>
                  <div className="wa-preview-screen">
                    <div className="wa-preview-bubble">
                      <p className="wa-preview-text" style={{ whiteSpace: "pre-wrap" }}>
                        {previewText || <em style={{ color: "#9ca3af" }}>Fill in params to see preview...</em>}
                      </p>
                      <span className="wa-preview-meta">10:30 AM ✓✓</span>
                    </div>
                  </div>
                </div>
              )}

              <span className="notif-file-hint">
                Will be sent to all {studentCount} student contact numbers
              </span>
            </div>
          )}

          {/* Result summary */}
          {result && result.success && (
            <div className="bd-notif-result">
              <div className="bd-notif-result-counts">
                <span className="bd-notif-result-sent">✓ Sent: {result.sent}</span>
                {result.failed > 0 && (
                  <span className="bd-notif-result-failed">✕ Failed: {result.failed}</span>
                )}
              </div>
              {result.failed > 0 && result.details?.failed?.length > 0 && (
                <div className="bd-notif-failed-list">
                  <p className="bd-notif-failed-label">Could not reach:</p>
                  {result.details.failed.map((f, i) => (
                    <div key={i} className="bd-notif-failed-row">
                      <span className="bd-notif-failed-name">{f.name}</span>
                      <span className="bd-notif-failed-reason">{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {result && !result.success && (
            <div className="notif-status notif-status-error">{result.error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="notif-modal-footer">
          <button className="notif-cancel-btn" onClick={handleClose} disabled={isSending}>Cancel</button>
          <button className="notif-send-btn" onClick={handleSend} disabled={isSending || !canSend || sentTypes.has(notifType)}>
            {isSending ? "Sending…" : `Send ${notifType === "email" ? "Email" : "WhatsApp"} to All`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Batch Details Page ─────────────────────────────────────── */
const BatchDetails = () => {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [batchLogs, setBatchLogs] = useState([]);
  const [batchLogsLoading, setBatchLogsLoading] = useState(false);
  const [subjectTitleById, setSubjectTitleById] = useState({});
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const getBatch = async () => {
      const delay = new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const [data] = await Promise.all([fetchBatchById(id), delay]);
        setBatch(data);
      } catch (err) {
        setError("Error fetching batch details");
      } finally {
        setLoading(false);
      }
    };
    getBatch();
  }, [id]);

  useEffect(() => {
    const getStudents = async () => {
      setStudentsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/student?batchId=${id}&pageSize=500`);
        const response = await res.json();
        setStudents(response.data || []);
      } catch (err) {
        console.error("Error fetching batch students:", err);
      } finally {
        setStudentsLoading(false);
      }
    };
    getStudents();
  }, [id]);

  useEffect(() => {
    const getAssignments = async () => {
      setAssignmentsLoading(true);
      try {
        const data = await fetchAssignmentsByBatchId(id);
        setAssignments(data);
      } catch (err) {
        console.error("Error fetching batch assignments:", err);
      } finally {
        setAssignmentsLoading(false);
      }
    };
    getAssignments();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setBatchLogsLoading(true);
    fetchLogsByBatchId(id)
      .then((res) => setBatchLogs(res?.data || []))
      .catch(() => setBatchLogs([]))
      .finally(() => setBatchLogsLoading(false));
  }, [id]);

  useEffect(() => {
    fetchAllSubjectsSimple()
      .then((subjectsList) => {
        setSubjectTitleById(Object.fromEntries(subjectsList.map((s) => [s._id, s.title])));
      })
      .catch(() => setSubjectTitleById({}));
  }, []);

  if (loading) {
    return (
      <div className="bd-overlay">
        <div className="bd-loader"></div>
        <p className="bd-loading-text">Loading...</p>
      </div>
    );
  }

  if (error || !batch) {
    return <p className="bd-error-text">{error || "No batch data found"}</p>;
  }

  const trainer = batch.trainer || {};
  const notes = Array.isArray(batch.notes) ? batch.notes : [];
  const batchDays = Array.isArray(batch.batchDays) ? batch.batchDays : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAssignmentWindow = (timeStr) => {
    if (!timeStr || !timeStr.trim()) return "—";
    const parts = timeStr.split(" - ");
    if (parts.length === 2) {
      const start = parts[0].trim();
      const end = parts[1].trim();
      const fmt = (s) => {
        const d = new Date(s);
        return isNaN(d.getTime()) ? s : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
      };
      return `${fmt(start)} → ${fmt(end)}`;
    }
    return timeStr;
  };

  const DAY_ABBR = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };

  const InfoRow = ({ label, value }) => (
    <div className="bd-info-row">
      <span className="bd-info-label">{label}</span>
      <span className="bd-info-value">{value ?? "—"}</span>
    </div>
  );

  return (
    <div className="bd-container">

      <BatchNotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        batch={batch}
        studentCount={students.length}
        assignments={assignments}
      />

      {/* ── Page Header ── */}
      <div className="bd-header">
        <div className="bd-header-title">
          <div className="bd-avatar">{batch.batchTitle?.charAt(0)?.toUpperCase()}</div>
          <div>
            <h1 className="bd-title">{batch.batchTitle}</h1>
            <span className={`bd-status-badge bd-status-${batch.batchStatus?.toLowerCase()}`}>
              {batch.batchStatus}
            </span>
          </div>
        </div>
        <div className="bd-header-actions">
          <button
            className="notif-trigger-btn"
            onClick={() => setShowNotificationModal(true)}
          >
            Send Notification
          </button>
          <button
            className="bd-btn bd-btn-secondary"
            onClick={() => navigate(`/meeting/86874801127`)}
          >
            Start Class
          </button>
          <button className="bd-btn bd-btn-primary" onClick={() => navigate(`/batches/${batch._id}/update`)}>
            Edit Batch
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="bd-body">

        {/* ── Sidebar ── */}
        <div className="bd-sidebar">

          {/* Trainer Card */}
          <div className="bd-card">
            <p className="bd-card-heading">Trainer</p>
            {trainer?.trainerName ? (
              <div className="bd-trainer">
                {trainer.trainerProfilePicture ? (
                  <img
                    src={trainer.trainerProfilePicture}
                    alt={trainer.trainerName}
                    className="bd-trainer-pic"
                  />
                ) : (
                  <div className="bd-trainer-avatar">
                    {trainer.trainerName.charAt(0).toUpperCase()}
                  </div>
                )}
                <p className="bd-trainer-name">{trainer.trainerName}</p>
                {trainer.trainerCompany && (
                  <p className="bd-trainer-company">{trainer.trainerCompany}</p>
                )}
                <div className="bd-trainer-meta">
                  {trainer.trainerExperience && (
                    <InfoRow label="Experience" value={trainer.trainerExperience} />
                  )}
                  {trainer.trainerSkills && (
                    <InfoRow label="Skills" value={trainer.trainerSkills} />
                  )}
                </div>
              </div>
            ) : (
              <p className="bd-empty-text">No trainer assigned</p>
            )}
          </div>

          {/* Schedule Card */}
          <div className="bd-card">
            <p className="bd-card-heading">Schedule</p>
            <InfoRow label="Time" value={batch.batchTime || "—"} />
            <InfoRow label="Duration" value={batch.batchDuration || "—"} />
            <div className="bd-info-row">
              <span className="bd-info-label">Days</span>
              <div className="bd-day-chips">
                {batchDays.length > 0
                  ? batchDays.map((d) => (
                      <span key={d} className="bd-day-chip">{DAY_ABBR[d] || d}</span>
                    ))
                  : <span className="bd-empty-text">—</span>
                }
              </div>
            </div>
          </div>

          {/* Dates Card */}
          <div className="bd-card">
            <p className="bd-card-heading">Dates</p>
            <InfoRow label="Start Date" value={formatDate(batch.batchStartDate)} />
            <InfoRow label="End Date" value={formatDate(batch.batchEndDate)} />
            <InfoRow label="Created" value={formatDateTime(batch.createdDateTime)} />
            <InfoRow label="Updated" value={formatDateTime(batch.updatedDateTime)} />
          </div>
        </div>

        {/* ── Main Content ── */}
        <div className="bd-main">

          {/* About Card */}
          <div className="bd-card">
            <p className="bd-card-heading">About this Batch</p>
            <p className="bd-description">{batch.batchDescription || "No description provided."}</p>
          </div>

          {/* Subjects Card */}
          <div className="bd-card">
            <p className="bd-card-heading">
              Subjects ({(batch.subjects || []).filter((s) => s.subjectId).length})
            </p>
            {(batch.subjects || []).filter((s) => s.subjectId).length === 0 ? (
              <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>No subjects added. Edit this batch to add subjects.</p>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(batch.subjects || [])
                  .filter((s) => s.subjectId)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((s, i) => {
                    const subjectId = s.subjectId._id?.toString() || s.subjectId.toString();
                    return (
                      <span
                        key={subjectId}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "#ede9fe", color: "#5b21b6",
                          borderRadius: 20, padding: "5px 14px 5px 10px",
                          fontSize: 13, fontWeight: 600, border: "1px solid #c4b5fd",
                        }}
                      >
                        <span style={{ fontSize: 11, color: "#7c3aed", minWidth: 18 }}>#{i + 1}</span>
                        {s.subjectId.title || subjectTitleById[subjectId] || subjectId}
                      </span>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Notes Card */}
          <div className="bd-card">
            <p className="bd-card-heading">Notes ({notes.length})</p>
            {notes.length > 0 ? (
              <div className="bd-table-wrapper">
                <table className="bd-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Link</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((note) => (
                      <tr key={note._id}>
                        <td className="bd-note-title">{note.title}</td>
                        <td className="bd-note-desc">{note.description}</td>
                        <td>
                          {note.linkOfAttachment ? (
                            <a
                              href={note.linkOfAttachment}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bd-link"
                            >
                              Open ↗
                            </a>
                          ) : (
                            <span className="bd-empty-text">—</span>
                          )}
                        </td>
                        <td className="bd-note-date">
                          {note.dateTime ? new Date(note.dateTime).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td>
                          <span className={`bd-note-status bd-note-${note.status?.toLowerCase()}`}>
                            {note.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bd-empty-state">
                <span className="bd-empty-icon">📋</span>
                <p>No notes have been added yet</p>
              </div>
            )}
          </div>

          {/* Students Card */}
          <div className="bd-card">
            <p className="bd-card-heading">
              Students{!studentsLoading && ` (${students.length})`}
            </p>
            {studentsLoading ? (
              <div className="bd-students-loading">
                <div className="bd-students-spinner" />
                <span>Loading students…</span>
              </div>
            ) : students.length > 0 ? (
              <div className="bd-table-wrapper">
                <table className="bd-table bd-students-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>DOB</th>
                      <th>Feedback Count</th>
                      <th>Contact</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, index) => (
                      <tr
                        key={student._id}
                        className="bd-student-row"
                        onClick={() => navigate(`/students/${student._id}`)}
                      >
                        <td className="bd-student-serial">{index + 1}</td>
                        <td className="bd-student-name">{student.studentName}</td>
                        <td>
                          {student.status ? (
                            <span className={`bd-note-status bd-note-${student.status.toLowerCase()}`}>
                              {student.status}
                            </span>
                          ) : "—"}
                        </td>
                        <td>
                          {student.DOB
                            ? new Date(student.DOB).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                            : "—"}
                        </td>
                        <td>
                          <span className="bd-feedback-badge">{student.feedbackCount ?? 0}</span>
                        </td>
                        <td className="bd-student-contact">{student.contactNumber ?? "—"}</td>
                        <td className="bd-student-email">
                          {Array.isArray(student.emailId) && student.emailId.length > 0
                            ? student.emailId[0]
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bd-empty-state">
                <span className="bd-empty-icon">👥</span>
                <p>No students enrolled in this batch yet</p>
              </div>
            )}
          </div>

          {/* Assignments Card */}
          <div className="bd-card">
            <p className="bd-card-heading">
              Assignments{!assignmentsLoading && ` (${assignments.length})`}
            </p>
            {assignmentsLoading ? (
              <div className="bd-students-loading">
                <div className="bd-students-spinner" />
                <span>Loading assignments…</span>
              </div>
            ) : assignments.length > 0 ? (
              <div className="bd-table-wrapper">
                <table className="bd-table bd-assignments-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Assignment Title</th>
                      <th>Created Date</th>
                      <th>Start → End</th>
                      <th>Status</th>
                      <th>Assignment Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((assignment, index) => (
                      <tr
                        key={assignment._id}
                        className="bd-student-row"
                        onClick={() => window.open(`/assignments/${assignment._id}`, "_blank")}
                      >
                        <td className="bd-student-serial">{index + 1}</td>
                        <td className="bd-assignment-title">{assignment.assignmentTitle}</td>
                        <td className="bd-note-date">
                          {assignment.createdDateTime
                            ? new Date(assignment.createdDateTime).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                            : "—"}
                        </td>
                        <td className="bd-assignment-window">
                          {formatAssignmentWindow(assignment.assignmentTime)}
                        </td>
                        <td>
                          <span className={`bd-assignment-status bd-assignment-status-${(assignment.status || "").toLowerCase()}`}>
                            {assignment.status || "—"}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <a
                            href={`${BUDDY_BASE}?id=${assignment._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bd-link"
                          >
                            Open ↗
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bd-empty-state">
                <span className="bd-empty-icon">📝</span>
                <p>No assignments added for this batch yet</p>
              </div>
            )}
          </div>

          {/* Activity Logs Card */}
          <div className="bd-card">
            <p className="bd-card-heading">
              Activity Logs{!batchLogsLoading && ` (${batchLogs.length})`}
            </p>
            {batchLogsLoading ? (
              <div className="bd-students-loading">
                <div className="bd-students-spinner" />
                <span>Loading logs…</span>
              </div>
            ) : batchLogs.length > 0 ? (
              <div className="bd-table-wrapper">
                <table className="bd-table bd-students-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Action</th>
                      <th>Page</th>
                      <th>Details</th>
                      <th>Date &amp; Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchLogs.map((log, index) => (
                      <tr key={log._id}>
                        <td className="bd-student-serial">{index + 1}</td>
                        <td
                          className="bd-student-name bd-student-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => log.studentId?._id && navigate(`/students/${log.studentId._id}`)}
                        >
                          {log.studentId?.studentName || "—"}
                        </td>
                        <td>
                          <span className={`sd-log-badge sd-log-${log.action}`}>
                            {log.action.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td>{log.page || "—"}</td>
                        <td>{log.details || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {new Date(log.timestamp).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bd-empty-state">
                <span className="bd-empty-icon">📋</span>
                <p>No activity logs for this batch yet</p>
              </div>
            )}
          </div>

          <NotificationHistorySection entityType="batch" entityId={id} />
        </div>
      </div>
    </div>
  );
};

export default BatchDetails;
